import { useEffect, useRef } from "react";

import { Animated, useWindowDimensions } from "react-native";
import type { ViewStyle } from "react-native";

import { AnimationDuration, ToastAnimation } from "@/constants/UI";
import { Text } from "@/ui/core";
import Icon, { FAIconName } from "@/ui/core/Icon";
import { HStack } from "@/ui/layout";
import { ShadowStyles } from "@/ui/theme/styles";
import { ColorToken, LayoutToken, ZIndexToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

type ToastVariant = "error" | "success";

const ToastVariantValues: Record<ToastVariant, ToastVariant> = {
  error: "error",
  success: "success",
};

interface ToastRequest {
  text: string;
  variant: ToastVariant;
}

interface ToastConfig extends ToastRequest {
  id: string;
}

interface ToastVariantProps {
  backgroundColor: string;
  icon: FAIconName;
  iconColor: string;
  textColor: string;
}

const ToastVariantMapper: Record<ToastVariant, ToastVariantProps> = {
  error: {
    backgroundColor: ColorToken.NotificationCriticalBackground,
    icon: FAIconName.Times,
    iconColor: ColorToken.NotificationCriticalText,
    textColor: ColorToken.NotificationCriticalText,
  },
  success: {
    backgroundColor: ColorToken.Primary10,
    icon: FAIconName.Check,
    iconColor: ColorToken.Primary100,
    textColor: ColorToken.NeutralPrimary,
  },
};

const Toast = ({
  positionStyle,
  toast,
}: {
  positionStyle: ViewStyle;
  toast: ToastConfig;
}) => {
  const opacityAnim = useRef(new Animated.Value(0)).current;
  const translateYAnim = useRef(
    new Animated.Value(ToastAnimation.TranslateY),
  ).current;

  const windowWidth = useWindowDimensions().width;
  const maxToastWidth = windowWidth - LayoutToken.ContentPaddingHorizontal * 2;

  useEffect(() => {
    // Animate in on mount
    Animated.parallel([
      Animated.timing(opacityAnim, {
        duration: AnimationDuration.Default,
        toValue: 1,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        useNativeDriver: true,
      }),
      Animated.timing(translateYAnim, {
        duration: AnimationDuration.Default,
        toValue: 0,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        useNativeDriver: true,
      }),
    ]).start();

    // Animate out and cleanup on unmount
    return () => {
      Animated.parallel([
        Animated.timing(opacityAnim, {
          duration: AnimationDuration.Default,
          toValue: 0,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          useNativeDriver: true,
        }),
        Animated.timing(translateYAnim, {
          duration: AnimationDuration.Default,
          toValue: ToastAnimation.TranslateY,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          useNativeDriver: true,
        }),
      ]).start();
    };
  }, [opacityAnim, translateYAnim]);

  const { backgroundColor, icon, iconColor, textColor } =
    ToastVariantMapper[toast.variant];

  return (
    <Animated.View
      style={[
        {
          alignItems: "center",
          alignSelf: "center",
          opacity: opacityAnim,
          position: "absolute",
          transform: [{ translateY: translateYAnim }],
          zIndex: ZIndexToken.Toast,
        },
        positionStyle,
      ]}
    >
      <HStack
        alignItems="center"
        backgroundColor={backgroundColor}
        borderRadius={12}
        gap={8}
        justifyContent="center"
        maxWidth={maxToastWidth}
        paddingHorizontal={12}
        paddingVertical={8}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...ShadowStyles}
      >
        <Icon color={iconColor} name={icon} size="sm" testID="toast " />
        <Text
          color={textColor}
          testID={joinTestIDs("toast", "text")}
          variant="captionMedium"
        >
          {toast.text}
        </Text>
      </HStack>
    </Animated.View>
  );
};

export default Toast;
export { ToastVariantValues };
export type { ToastConfig, ToastRequest, ToastVariant };
